import { GlobalConfigType } from 'actions'
import { useCompanyLogo } from 'hooks/useTenant'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { logoDisplaySize } from './logoDisplay'

/**
 * `logo.png` -> `logo-dark.png`, for a logo whose artwork is dark ink and would
 * otherwise disappear on a dark canvas. Returns null when the url has no usable
 * extension, so we never fire a request that cannot succeed.
 */
const darkVariantUrl = (url: string): string | null => {
  const match = /^(.*)\.(png|jpg|jpeg|svg|webp|gif)(\?.*)?$/i.exec(url)
  if (!match) return null
  const [, base, ext, query = ''] = match
  return `${base}-dark.${ext}${query}`
}

/**
 * Picks the artwork to show for a logo under the active site style.
 *
 * On a dark style we prefer a `-dark` variant of the same file. Not every tenant
 * ships one, so if it fails to load we fall back to the light artwork on a white
 * chip — without the chip a dark-ink logo is invisible on a dark canvas. On a
 * light style it is always the plain artwork, no chip.
 */
export const useThemedLogo = (logoUrl: string) => {
  const style = useSelector((state: any) => state.auth?.profile?.configuration?.style)

  // Falls back to the live <html data-theme>, which index.tsx sets from
  // localStorage before Redux rehydrates — otherwise the logo flips once the
  // profile loads.
  const activeStyle =
    (typeof style === 'string' && style) ||
    (typeof document !== 'undefined' ? document.documentElement.getAttribute('data-theme') : '') ||
    ''
  const isDark = activeStyle.endsWith('dark')

  const darkUrl = isDark && logoUrl ? darkVariantUrl(logoUrl) : null
  const [darkMissing, setDarkMissing] = useState(false)
  useEffect(() => setDarkMissing(false), [logoUrl])

  const useDarkArtwork = Boolean(darkUrl) && !darkMissing
  return {
    src: useDarkArtwork ? (darkUrl as string) : logoUrl,
    wrapperClass: isDark && !useDarkArtwork ? 'inline-block rounded-md bg-white px-2 py-1' : 'inline-block',
    onError: useDarkArtwork ? () => setDarkMissing(true) : undefined,
  }
}

/** Renders a company logo sized from its aspect ratio (wide wordmark vs square mark). */
export const SizedLogoImg = ({ src, alt = 'Logo', onError }: { src: string; alt?: string; onError?: () => void }) => {
  const [size, setSize] = useState<{ width: number; height: number } | null>(null)

  useEffect(() => {
    setSize(null)
  }, [src])

  return (
    <img
      src={src}
      alt={alt}
      className="object-contain"
      style={size ? { width: size.width, height: size.height, maxWidth: '100%' } : { maxHeight: 50, maxWidth: 320 }}
      onError={onError}
      onLoad={(e) => {
        const img = e.currentTarget
        setSize(logoDisplaySize(img.naturalWidth, img.naturalHeight))
      }}
    />
  )
}

export const LogoBlue = () => {
  const { hostWhiteLabel, brokerDetail } = useSelector((state: any) => ({
    hostWhiteLabel: state.globalConfigReducer?.[GlobalConfigType.HostWhiteLabel],
    brokerDetail: state.auth?.profile?.brokerDetail,
  }))

  // Host vanity branding wins; else logged-in broker white label; else company logo.
  // White label on with no logo → show nothing.
  const brand =
    hostWhiteLabel && typeof hostWhiteLabel === 'object' ? hostWhiteLabel : brokerDetail != null ? brokerDetail : null
  const isWhiteLabel = brand != null
  // Read at render time so a tenant's logo change lands without signing in again.
  const companyLogo = useCompanyLogo()
  const logoUrl = isWhiteLabel ? brand.logoUrl || '' : companyLogo

  const { src, wrapperClass, onError } = useThemedLogo(logoUrl)

  if (!logoUrl) return null

  return (
    <span className={wrapperClass}>
      <SizedLogoImg src={src} alt="Logo" onError={onError} />
    </span>
  )
}
